import React, { useState, useEffect, useCallback, useMemo } from 'react';
import {
    Search, Loader2, CheckCircle2, XCircle, AlertTriangle,
    Calendar, FileText, Factory, Award, Info,
    ClipboardCheck, ExternalLink, Building2, Layers, SearchX
} from 'lucide-react';

const API_URL = process.env.REACT_APP_API_URL;

// Статические типы СИ (Тепловычислители остаются захардкожены)
const STATIC_DEVICE_TYPES = [
    { id: 'karat-307', name: 'Карат-307', mitNumber: '61255-15', category: 'Тепловычислители', hasDeviceList: true },
    { id: 'aksioma-01', name: 'Аксиома-01', mitNumber: '53677-13', category: 'Тепловычислители', hasDeviceList: true },
    { id: 'aksioma-03', name: 'Аксиома-03', mitNumber: '53677-13', category: 'Тепловычислители', hasDeviceList: true },
];

// Маппинг типа из БД (1, 2, 3) в название категории для группировки
const getCategoryName = (type) => {
    switch (type) {
        case 1: case '1': case 'Temperature': return 'Датчики температуры';
        case 2: case '2': case 'Pressure': return 'Датчики давления';
        case 3: case '3': case 'Flow': return 'Датчики объёмного расхода';
        default: return 'Датчики';
    }
};

const FgisArshinTab = () => {
    const [deviceType, setDeviceType] = useState('karat-307');
    const [serialNumber, setSerialNumber] = useState('');
    const [loading, setLoading] = useState(false);
    const [result, setResult] = useState(null);
    const [error, setError] = useState('');
    const [searchedSerial, setSearchedSerial] = useState('');

    const [devices, setDevices] = useState([]);
    const [devicesLoading, setDevicesLoading] = useState(false);
    const [showDeviceList, setShowDeviceList] = useState(false);

    // Состояния для загрузки справочников
    const [sensorTypes, setSensorTypes] = useState([]);
    const [dictLoading, setDictLoading] = useState(true);

    // Загрузка типов СИ из справочников
    useEffect(() => {
        const fetchDictionaries = async () => {
            setDictLoading(true);
            try {
                const token = localStorage.getItem('token');
                const res = await fetch(`${API_URL}/api/sensor-dictionaries`, {
                    headers: { Authorization: `Bearer ${token}` }
                });
                if (res.ok) {
                    const data = await res.json();
                    const mapped = data.map(item => ({
                        id: `sensor-${item.id}`,
                        name: item.name,
                        designation: item.designation, // ✅ Добавляем обозначение
                        mitNumber: item.registryNumber,
                        category: getCategoryName(item.type),
                        hasDeviceList: false
                    }));
                    setSensorTypes(mapped);
                } else {
                    console.warn("Не удалось загрузить справочники СИ");
                }
            } catch (err) {
                console.error("Ошибка загрузки справочников:", err);
            } finally {
                setDictLoading(false);
            }
        };
        fetchDictionaries();
    }, []);

    // Объединяем статические устройства и загруженные датчики
    const allDeviceTypes = useMemo(() => {
        return [...STATIC_DEVICE_TYPES, ...sensorTypes];
    }, [sensorTypes]);

    // Загрузка устройств при смене типа
    const loadDevices = useCallback(async (typeId) => {
        const dt = allDeviceTypes.find(d => d.id === typeId);
        if (!dt?.hasDeviceList) {
            setDevices([]);
            return;
        }

        setDevicesLoading(true);
        try {
            const token = localStorage.getItem('token');
            const res = await fetch(
                `${API_URL}/api/fgis-arshin/devices?deviceType=${encodeURIComponent(dt.name)}`,
                { headers: { Authorization: `Bearer ${token}` } }
            );
            if (res.ok) {
                const data = await res.json();
                setDevices(data);
            } else {
                console.warn(`[FGIS] Не удалось загрузить устройства для ${dt.name}`);
                setDevices([]);
            }
        } catch (err) {
            console.error('Ошибка загрузки устройств:', err);
            setDevices([]);
        } finally {
            setDevicesLoading(false);
        }
    }, [allDeviceTypes]);

    useEffect(() => {
        loadDevices(deviceType);
    }, [deviceType, loadDevices]);

    useEffect(() => {
        const handleClickOutside = () => setShowDeviceList(false);
        if (showDeviceList) {
            document.addEventListener('click', handleClickOutside);
            return () => document.removeEventListener('click', handleClickOutside);
        }
    }, [showDeviceList]);

    const handleDeviceSelect = (device) => {
        const serial = device.serialNumber || device.name || '';
        setSerialNumber(serial);
        setShowDeviceList(false);
    };

    const handleSearch = async (e) => {
        e?.preventDefault();
        if (!serialNumber.trim()) {
            setError('Введите заводской номер');
            return;
        }

        setSearchedSerial(serialNumber.trim());
        setLoading(true);
        setError('');
        setResult(null);

        try {
            const token = localStorage.getItem('token');
            const selectedDevice = allDeviceTypes.find(d => d.id === deviceType);
            const url = `${API_URL}/api/fgis-arshin/verify?serialNumber=${encodeURIComponent(serialNumber.trim())}&mitNumber=${encodeURIComponent(selectedDevice?.mitNumber || '')}`;

            const res = await fetch(url, {
                headers: { Authorization: `Bearer ${token}` }
            });
            const data = await res.json();

            if (res.ok) {
                setResult(data);
            } else {
                setError(data.message || 'Не удалось получить данные из ФГИС Аршин');
            }
        } catch (err) {
            console.error(err);
            setError('Ошибка соединения с сервером');
        } finally {
            setLoading(false);
        }
    };

    const formatDate = (dateString) => {
        if (!dateString) return '—';
        const date = new Date(dateString);
        return date.toLocaleDateString('ru-RU', { day: '2-digit', month: '2-digit', year: 'numeric' });
    };

    const getDaysLeft = (validDate) => {
        if (!validDate) return null;
        const diffTime = new Date(validDate) - new Date();
        return Math.ceil(diffTime / (1000 * 60 * 60 * 24));
    };

    const selectedDevice = allDeviceTypes.find(d => d.id === deviceType);

    const getFullMitTitle = () => {
        if (!result) return '';
        let title = result.mitTitle || '';
        if (result.mitNotation && result.mitNotation !== title) title += ' ' + result.mitNotation;
        return title.trim();
    };

    const groupedDeviceTypes = useMemo(() => {
        const groups = {};
        allDeviceTypes.forEach(dt => {
            if (!groups[dt.category]) groups[dt.category] = [];
            groups[dt.category].push(dt);
        });
        return groups;
    }, [allDeviceTypes]);

    const isNotFound = result && !result.isExpired && result.isApplicable !== false &&
        (!result.serialNumber || result.message?.includes('не найдены'));

    const fgisLink = result?.vriId
        ? `https://fgis.gost.ru/fundmetrology/cm/results/${result.vriId}`
        : 'https://fgis.gost.ru/fundmetrology/cm/results';

    return (
        <div className="bg-slate-800 rounded-xl p-4 sm:p-6 border border-slate-700">
            {/* Заголовок */}
            <div className="flex items-center gap-3 mb-5">
                <div className="w-10 h-10 rounded-lg bg-blue-600/20 flex items-center justify-center">
                    <ClipboardCheck className="w-5 h-5 text-blue-400" />
                </div>
                <div>
                    <h3 className="text-white text-lg font-semibold">Проверка поверок ФГИС «Аршин»</h3>
                    <p className="text-slate-400 text-sm">
                        Получение данных из Федерального информационного фонда по обеспечению единства измерений
                    </p>
                </div>
            </div>

            {/* Форма поиска */}
            <form onSubmit={handleSearch} className="grid grid-cols-1 md:grid-cols-12 gap-3 mb-5">
                <div className="md:col-span-4">
                    <label className="block text-sm font-medium text-slate-300 mb-1.5">Тип СИ</label>
                    <select
                        value={deviceType}
                        onChange={(e) => setDeviceType(e.target.value)}
                        className="w-full bg-slate-700 text-white rounded-lg px-3 py-2.5 border border-slate-600 focus:border-blue-500 focus:outline-none text-sm"
                        disabled={dictLoading && sensorTypes.length === 0}
                    >
                        {dictLoading && sensorTypes.length === 0 && (
                            <option value="karat-307">Загрузка справочников...</option>
                        )}
                        {Object.entries(groupedDeviceTypes).map(([category, types]) => (
                            <optgroup key={category} label={category}>
                                {types.map(dt => (
                                    <option key={dt.id} value={dt.id}>
                                        {/* ✅ Для датчиков используем designation, для тепловычислителей - name */}
                                        {dt.designation || dt.name} (рег. № {dt.mitNumber})
                                    </option>
                                ))}
                            </optgroup>
                        ))}
                    </select>
                </div>

                <div className="md:col-span-6 relative">
                    <label className="block text-sm font-medium text-slate-300 mb-1.5">Заводской / серийный номер</label>
                    <div className="flex gap-2">
                        <input
                            type="text"
                            value={serialNumber}
                            onChange={(e) => setSerialNumber(e.target.value)}
                            placeholder="Введите заводской номер прибора..."
                            className="flex-1 px-3 py-2.5 bg-slate-700 border border-slate-600 rounded-lg text-white text-sm placeholder-slate-500 focus:outline-none focus:ring-2 focus:ring-blue-500"
                        />
                        {selectedDevice?.hasDeviceList && (
                            <button
                                type="button"
                                onClick={(e) => { e.stopPropagation(); setShowDeviceList(!showDeviceList); }}
                                disabled={devicesLoading}
                                className="px-3 py-2.5 bg-slate-700 hover:bg-slate-600 border border-slate-600 text-slate-300 rounded-lg transition disabled:opacity-50"
                                title={`Выбрать из списка (${devices.length} шт.)`}
                            >
                                {devicesLoading ? (
                                    <Loader2 className="w-4 h-4 animate-spin" />
                                ) : (
                                    <Factory className="w-4 h-4" />
                                )}
                            </button>
                        )}
                    </div>

                    {/* Выпадающий список устройств */}
                    {showDeviceList && selectedDevice?.hasDeviceList && (
                        <div
                            className="absolute z-20 mt-1 w-full max-h-60 overflow-y-auto bg-slate-700 border border-slate-600 rounded-lg shadow-xl"
                            onClick={(e) => e.stopPropagation()}
                        >
                            {devicesLoading ? (
                                <div className="px-3 py-4 text-center text-slate-400 text-sm flex items-center justify-center gap-2">
                                    <Loader2 className="w-4 h-4 animate-spin" />
                                    Загрузка...
                                </div>
                            ) : devices.length === 0 ? (
                                <div className="px-3 py-4 text-center text-slate-400 text-sm">
                                    Нет устройств типа «{selectedDevice?.name}» в базе
                                </div>
                            ) : (
                                devices.map((d, idx) => (
                                    <button
                                        key={idx}
                                        type="button"
                                        onClick={() => handleDeviceSelect(d)}
                                        className="w-full text-left px-3 py-2 hover:bg-slate-600 text-white text-sm border-b border-slate-600 last:border-0 transition"
                                    >
                                        <div className="flex justify-between items-center">
                                            <span className="font-medium">{d.city && <span>{d.city}, </span>}
                                                {d.street} {d.houseNumber}</span>
                                            {d.serialNumber && (
                                                <span className="text-xs font-mono text-blue-400 ml-2">
                                                    {d.serialNumber}
                                                </span>
                                            )}
                                        </div>
                                        <div className="text-xs text-slate-400 mt-0.5">
                                            {d.name}
                                        </div>
                                    </button>
                                ))
                            )}
                        </div>
                    )}
                </div>

                <div className="md:col-span-2 flex items-end">
                    <button
                        type="submit"
                        disabled={loading || !serialNumber.trim()}
                        className="w-full flex items-center justify-center gap-2 px-4 py-2.5 bg-blue-600 hover:bg-blue-700 disabled:opacity-50 disabled:cursor-not-allowed text-white font-medium rounded-lg transition text-sm"
                    >
                        {loading ? (
                            <><Loader2 className="w-4 h-4 animate-spin" /> Поиск...</>
                        ) : (
                            <><Search className="w-4 h-4" /> Найти</>
                        )}
                    </button>
                </div>
            </form>


            {error && (
                <div className="px-4 py-3 mb-5 bg-red-600/20 border border-red-600/40 rounded-lg text-red-300 text-sm flex items-start gap-2">
                    <AlertTriangle className="w-5 h-5 flex-shrink-0 mt-0.5" />
                    <span>{error}</span>
                </div>
            )}

            {/* Результат поиска */}
            {result && (
                <div className="space-y-4">
                    <div className={`rounded-xl p-5 border ${isNotFound
                        ? 'bg-slate-700/30 border-slate-600'
                        : result.isExpired
                            ? 'bg-red-900/20 border-red-700'
                            : result.isApplicable === false
                                ? 'bg-orange-900/20 border-orange-700'
                                : 'bg-green-900/20 border-green-700'
                        }`}>
                        <div className="flex items-start gap-4">
                            <div className={`w-14 h-14 rounded-full flex items-center justify-center shrink-0 ${isNotFound
                                ? 'bg-slate-600/30'
                                : result.isExpired
                                    ? 'bg-red-600/30'
                                    : result.isApplicable === false
                                        ? 'bg-orange-600/30'
                                        : 'bg-green-600/30'
                                }`}>
                                {isNotFound ? (
                                    <SearchX className="w-8 h-8 text-slate-400" />
                                ) : result.isExpired ? (
                                    <XCircle className="w-8 h-8 text-red-400" />
                                ) : result.isApplicable === false ? (
                                    <AlertTriangle className="w-8 h-8 text-orange-400" />
                                ) : (
                                    <CheckCircle2 className="w-8 h-8 text-green-400" />
                                )}
                            </div>
                            <div className="flex-1">
                                <h4 className={`text-xl font-bold mb-1 ${isNotFound
                                    ? 'text-slate-300'
                                    : result.isExpired
                                        ? 'text-red-300'
                                        : result.isApplicable === false
                                            ? 'text-orange-300'
                                            : 'text-green-300'
                                    }`}>
                                    {isNotFound ? 'Поверки не найдены' : (result.statusText || 'Информация о поверке')}
                                </h4>

                                {!isNotFound && result.validDate && !result.isExpired && (
                                    <p className="text-slate-400 text-xs mt-1">
                                        {(() => {
                                            const days = getDaysLeft(result.validDate);
                                            if (days !== null) {
                                                if (days > 365) return `Осталось более ${Math.floor(days / 365)} лет`;
                                                if (days > 30) return `Осталось ${Math.floor(days / 30)} мес. ${days % 30} дн.`;
                                                return `Осталось ${days} дн.`;
                                            }
                                            return '';
                                        })()}
                                    </p>
                                )}

                                {isNotFound && (
                                    <p className="text-slate-400 text-sm mt-1">
                                        По запросу с заводским номером{' '}
                                        <span className="font-mono text-slate-300">{searchedSerial}</span>{' '}
                                        записей не обнаружено
                                    </p>
                                )}
                            </div>
                        </div>
                    </div>

                    {!isNotFound && (
                        <>
                            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                                <div className="bg-slate-700/40 rounded-xl p-4 border border-slate-700">
                                    <h5 className="text-white font-semibold text-sm uppercase tracking-wider mb-3 flex items-center gap-2">
                                        <Award className="w-4 h-4 text-blue-400" />
                                        Основные данные
                                    </h5>
                                    <div className="space-y-3">
                                        <div>
                                            <div className="text-slate-400 text-xs mb-0.5">Заводской номер</div>
                                            <div className="text-white font-mono text-sm">{result.serialNumber || '—'}</div>
                                        </div>
                                        <div>
                                            <div className="text-slate-400 text-xs mb-0.5">Рег. номер типа СИ</div>
                                            <div className="text-white font-mono text-sm">{result.mitNumber || '—'}</div>
                                        </div>
                                        <div>
                                            <div className="text-slate-400 text-xs mb-0.5">Год выпуска СИ</div>
                                            <div className="text-white font-semibold">{result.manufactureYear || '—'}</div>
                                        </div>
                                        <div>
                                            <div className="text-slate-400 text-xs mb-0.5">Номер свидетельства</div>
                                            <div className="text-white font-mono text-sm">
                                                {result.certificateNumber && result.certificateNumber !== 'Нет данных'
                                                    ? result.certificateNumber : '—'}
                                            </div>
                                        </div>
                                    </div>
                                </div>

                                <div className="bg-slate-700/40 rounded-xl p-4 border border-slate-700">
                                    <h5 className="text-white font-semibold text-sm uppercase tracking-wider mb-3 flex items-center gap-2">
                                        <Layers className="w-4 h-4 text-blue-400" />
                                        Тип и модификация
                                    </h5>
                                    <div className="space-y-3">
                                        <div>
                                            <div className="text-slate-400 text-xs mb-0.5">Наименование типа СИ</div>
                                            <div className="text-white font-semibold text-sm">
                                                {getFullMitTitle() || '—'}
                                            </div>
                                        </div>
                                        <div>
                                            <div className="text-slate-400 text-xs mb-0.5">Модификация</div>
                                            <div className="text-white font-mono text-sm bg-slate-800/50 px-2 py-1 rounded">
                                                {result.miModification || '—'}
                                            </div>
                                        </div>
                                    </div>
                                </div>
                            </div>

                            <div className="bg-slate-700/40 rounded-xl p-4 border border-slate-700">
                                <h5 className="text-white font-semibold text-sm uppercase tracking-wider mb-3 flex items-center gap-2">
                                    <Calendar className="w-4 h-4 text-blue-400" />
                                    Сроки и организация
                                </h5>
                                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                                    <div className="space-y-3">
                                        <div>
                                            <div className="text-slate-400 text-xs mb-0.5">Дата поверки</div>
                                            <div className="text-white font-semibold">{formatDate(result.verificationDate)}</div>
                                        </div>
                                        <div>
                                            <div className="text-slate-400 text-xs mb-0.5">Действительна до</div>
                                            <div className={`font-semibold ${result.isExpired ? 'text-red-400' : 'text-green-400'}`}>
                                                {formatDate(result.validDate)}
                                            </div>
                                        </div>
                                        <div>
                                            <div className="text-slate-400 text-xs mb-0.5">Тип поверки</div>
                                            <div className="text-white text-sm">
                                                {result.vriType === '1' ? 'Первичная' :
                                                    result.vriType === '2' ? 'Периодическая' : '—'}
                                            </div>
                                        </div>
                                    </div>
                                    <div className="space-y-3">
                                        <div>
                                            <div className="text-slate-400 text-xs mb-0.5">Организация-поверитель</div>
                                            <div className="text-white text-sm flex items-start gap-1">
                                                <Building2 className="w-4 h-4 text-slate-500 shrink-0 mt-0.5" />
                                                <span className="line-clamp-2">{result.organization || '—'}</span>
                                            </div>
                                        </div>
                                    </div>
                                </div>
                            </div>

                            {result.docTitle && (
                                <div className="bg-slate-700/40 rounded-xl p-4 border border-slate-700">
                                    <div className="flex items-start gap-2">
                                        <FileText className="w-4 h-4 text-slate-500 shrink-0 mt-0.5" />
                                        <div>
                                            <div className="text-slate-400 text-xs mb-1">Документ-основание</div>
                                            <div className="text-white text-sm">{result.docTitle}</div>
                                        </div>
                                    </div>
                                </div>
                            )}
                        </>
                    )}

                    <div className="flex justify-end">
                        <a
                            href={fgisLink}
                            target="_blank"
                            rel="noopener noreferrer"
                            className="inline-flex items-center gap-2 text-blue-400 hover:text-blue-300 text-sm transition"
                        >
                            <ExternalLink className="w-4 h-4" />
                            {result?.vriId ? 'Открыть запись в реестре ФГИС Аршин' : 'Открыть реестр ФГИС Аршин'}
                        </a>
                    </div>
                </div>
            )}

            {!result && !error && !loading && (
                <div className="text-center py-8 text-slate-500">
                    <Search className="w-12 h-12 mx-auto mb-3 opacity-30" />
                    <p className="text-sm">
                        Введите заводской номер прибора или выберите из списка<br />
                        для проверки наличия поверки в реестре ФГИС «Аршин»
                    </p>
                </div>
            )}
        </div>
    );
};

export default FgisArshinTab;